/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * The 10-step disclosure pipeline:
 *
 *   1. generateSymmetricKey   — fresh document key (DK) + UUID id
 *   2. saveSymmetricKey       — persist DK in the vault + save vault (#247)
 *   3. encryptStory           — XChaCha20-Poly1305 with AAD bound to (id, version)
 *   4. requestPublicKeys      — F12: fetch + verify signed bundles; TOFU-pin
 *   5. signStory              — F5c: ML-DSA-65 over plaintext
 *   6. encryptSymmetricKeys   — F14: routing sheet wraps DK for all recipients
 *   7. saveDisclosureLocal    — persist recovery record in localStorage (#247)
 *   8. saveStoryServer        — POST /api/story  (encrypted content)
 *   9. saveDisclosureServer   — POST /api/disclosure (routing sheet + metadata)
 *   10. cleanup               — drop local recovery record; save vault if dirty
 *
 * Resumable: tickler tracks per-step status + data. On failure, the failing
 * step is marked and the result is `{success: false, tickler, error}` so
 * callers can retry with the same tickler and skip completed steps.
 */

import { sha256 } from '@noble/hashes/sha2.js';
import { base64nopad } from '@scure/base';
import { tdFetch } from './tdfetch.js';
import {
  createStoryProcessingTickler,
  StoryProcessingTickler,
  type StepName,
  type DocumentKey,
  type EncryptedStoryStepData,
  type PublicKeysBundle,
  type VerifiedKeyRecord,
  type SavedStoryResult,
  type SavedDisclosureResult,
} from './StoryProcessingTickler.js';
import {
  vaultService,
  storeSymmetricKey,
  retrievePrivateKey,
  signContent,
} from './vault.js';
import type { VaultContents } from './vault/schema.js';
import {
  ALGORITHM_ID as DOCUMENT_ALGORITHM_ID,
  generateDocumentKey,
  encryptDocumentContent,
} from './document/encrypt.js';
import { encodeStoryPlaintext } from './document/storyplaintext.js';
import {
  fetchSignedBundle,
  SYSTEM_IDENTITY,
  type SignedBundleFetcher,
  type SignedKeyBundle,
} from './keys/lookup.js';
import { pinIfNew } from './keys/tofu.js';
import { fingerprint as makeFingerprint } from './keys/fingerprint.js';
import {
  buildRoutingSheet,
  type RoutingSheet,
} from './document/routingsheet.js';

// -----------------------------------------------------------------------------
// Public types
// -----------------------------------------------------------------------------

/** Form-side participant stub as it arrives from `TdStoryForm.harvestData`. */
export interface ParticipantStub {
  name: string;
  email: string;
  confirmed?: boolean;
  signingKey?: Uint8Array;
}

/**
 * Submission shape `processStory` accepts. Optional fields are tolerated for
 * back-compat with test fixtures and the in-flight refactor; the active set
 * is what `TdStoryForm.harvestData` currently produces.
 */
export interface StorySubmission {
  content: string;
  /**
   * Story title (#240). Travels *inside* the encrypted plaintext
   * envelope (`document/storyplaintext.ts`) — never as cleartext
   * metadata. Defaults to '' when the form omits it.
   */
  title?: string;
  storyid?: string | null;
  keyid?: string | null;
  participants?: ParticipantStub[];
  whenType?: string;
  checkInValue?: number;
  checkInUnit?: string;
  /** Optional override for the author identity — falls back to active vault identity. */
  authorId?: string;
  /** Reserved for future fields the form may carry; unknown keys flow through. */
  [key: string]: unknown;
}

/** Result of a `processStory` invocation. Tickler is always returned so the caller can resume on failure. */
export type ProcessStoryResult =
  | { success: true; tickler: StoryProcessingTickler }
  | { success: false; tickler: StoryProcessingTickler; error: Error };

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

interface StoryPayloadInput {
  nonce: Uint8Array;
  ciphertext: Uint8Array;
  documentId: string;
  version: number;
}

interface StoryPayload {
  content: string;
  checksum: string;
  documentId: string;
  version: number;
  algorithmId: typeof DOCUMENT_ALGORITHM_ID;
}

/**
 * Creates a JSON payload with body and checksum for story submission.
 *
 * Includes the F13 document context (`documentId`, `version`, `algorithmId`)
 * so the recipient can reconstruct the AAD bytes that were bound at
 * encrypt-time. The server treats these as opaque metadata; AAD
 * reconstruction is purely client-side.
 */
const createStoryPayload = ({
  nonce,
  ciphertext,
  documentId,
  version,
}: StoryPayloadInput): StoryPayload => {
  // Combine nonce and ciphertext for the on-the-wire `content` blob.
  // The recipient splits at NONCE_LENGTH_BYTES (24) to recover both.
  const combined = new Uint8Array(nonce.length + ciphertext.length);
  combined.set(nonce, 0);
  combined.set(ciphertext, nonce.length);

  return {
    content: base64nopad.encode(combined),
    checksum: base64nopad.encode(sha256(combined)),
    documentId,
    version,
    algorithmId: DOCUMENT_ALGORITHM_ID,
  };
};

/**
 * Generate a fresh document key (32 bytes via the F3 RNG seam) and a
 * UUID id used as both the DK identifier and the F13 documentId.
 */
const generateStorySymmetricKey = async (): Promise<DocumentKey> => {
  const id = crypto.randomUUID();
  const key = generateDocumentKey();
  console.log('Generated document key:', id);
  return { id, key };
};

/**
 * Persist the DK durably (#247): store it in the unlocked vault's
 * `symmetricKeys`, then save the vault — the save PUTs the re-encrypted
 * envelope to the server (rollback counter bumped, If-Match sent) and
 * mirrors it into the local envelope backup. Without the save, a
 * browser crash after the story upload would lose the only copy of the
 * DK and the story would be undecryptable by the author.
 */
const saveSymmetricKeyToVault = async (
  keyObject: DocumentKey
): Promise<void> => {
  storeSymmetricKey(keyObject);
  await vaultService.saveVault();
};

/**
 * Encrypts story content under a fresh nonce, AAD-bound to
 * `(documentId, version, ALGORITHM_ID)` per F13. Caller must keep the
 * returned `nonce` for transport — it travels alongside the ciphertext.
 */
const encryptStoryContent = async (
  content: string,
  dk: Uint8Array,
  documentId: string,
  version: number
): Promise<{ nonce: Uint8Array; ciphertext: Uint8Array }> => {
  const { nonce, ciphertext } = encryptDocumentContent({
    content: new TextEncoder().encode(content),
    dk,
    documentId,
    version,
  });
  console.log('Story content encrypted');
  return { nonce, ciphertext };
};

/**
 * Build a per-identity record holding the F14 inputs the routing-sheet
 * builder consumes: the ML-KEM-768 public key (for KEM-encapsulate) plus
 * the SHA-256 fingerprint of the signing public key (the F12 TOFU pin,
 * AAD-bound into each routing-sheet entry).
 */
const verifiedKeyRecordFor = (bundle: SignedKeyBundle): VerifiedKeyRecord => ({
  kemPublicKey: bundle.keyValue,
  signingKeyFingerprint: makeFingerprint(bundle.signingKey),
});

/**
 * Fetch system + author + per-participant signed key bundles and pin each
 * via TOFU before returning the verified encryption keys.
 *
 * F12-wiring (#83) — every fetch routes through `fetchSignedBundle` which
 * validates the ML-DSA-65 self-signature, and through `pinIfNew` which
 * records the signing-key fingerprint on first contact or re-verifies it
 * against the existing pin on every later call. Threads immutable
 * `VaultContents` through each pin call so a `KeyChangedError` mid-loop
 * leaves no partial pin behind.
 *
 * Errors are not swallowed:
 *   - `BundleSignatureError` — bad signature, malformed shape, unsupported algorithm
 *   - `KeyChangedError` — signing key for an already-pinned identity does not match
 *   - `BundleFetchError` — non-2xx HTTP or non-JSON body
 *
 * @param contents Current vault contents (must be the unlocked snapshot
 *   from `vaultService.currentContents()`).
 * @param participants Participant stubs from the form.
 * @param authorId Author identity id (matches the backend lookup key).
 */
const requestPublicKeys = async (
  contents: VaultContents,
  participants: ParticipantStub[],
  authorId: string
): Promise<{ publicKeys: PublicKeysBundle; contents: VaultContents }> => {
  console.log(
    'Requesting signed public-key bundles for author and participants'
  );

  // `SignedBundleFetcher` declares its `init` parameter as `RequestInit`,
  // whereas `tdFetch` accepts a narrower `TdFetchOptions`. The actual
  // implementation in `fetchSignedBundle` never passes an `init` argument,
  // so the structural mismatch is harmless at runtime. Cast localizes the
  // strict-TS divergence to the boundary.
  const fetcher = tdFetch as unknown as SignedBundleFetcher;

  let working = contents;
  const participantsRecord: Record<string, VerifiedKeyRecord> = {};

  const systemBundle = await fetchSignedBundle(fetcher, SYSTEM_IDENTITY);
  ({ contents: working } = pinIfNew(working, systemBundle));
  const system = verifiedKeyRecordFor(systemBundle);

  const authorBundle = await fetchSignedBundle(fetcher, authorId);
  ({ contents: working } = pinIfNew(working, authorBundle));
  const author = verifiedKeyRecordFor(authorBundle);

  for (const participant of participants) {
    const participantBundle = await fetchSignedBundle(
      fetcher,
      participant.email
    );
    ({ contents: working } = pinIfNew(working, participantBundle));
    participantsRecord[participant.email] =
      verifiedKeyRecordFor(participantBundle);
  }

  console.log('Signed bundles verified and pinned for all identities');
  return {
    publicKeys: {
      author,
      system,
      participants: participantsRecord,
      authorId,
    },
    contents: working,
  };
};

/**
 * Sign the story plaintext with the active identity's ML-DSA-65 key. F5c
 * (#65) makes the active-identity lookup `signContent`'s problem, so this
 * helper no longer threads `authorId` through.
 */
const signStoryContent = async (content: string) => {
  console.log('Signing story content with active identity');
  return signContent(new TextEncoder().encode(content));
};

interface BuildSubmissionRoutingSheetParams {
  keyObject: DocumentKey;
  publicKeys: PublicKeysBundle;
  participants: ParticipantStub[];
}

/**
 * Build the F14 routing sheet wrapping `keyObject.key` for the author,
 * every participant, and the system identity.
 *
 * The author's ML-DSA-65 signing private key is read from the unlocked
 * vault via `retrievePrivateKey(authorId, 'sig')` — locked vault or
 * unknown identity surface as informative errors here rather than letting
 * `buildRoutingSheet` build a junk sheet.
 *
 * Participants are translated from form stubs (`{name, email}`) into the
 * F14 shape (`{identityId, kemPublicKey, signingKeyFingerprint}`) using
 * the verified records the F12 step produced.
 */
const buildSubmissionRoutingSheet = ({
  keyObject,
  publicKeys,
  participants,
}: BuildSubmissionRoutingSheetParams): RoutingSheet => {
  console.log('Building routing sheet for author + participants + system');
  const { authorId } = publicKeys;
  const signingPrivateKey = retrievePrivateKey(authorId, 'sig');
  if (signingPrivateKey === null) {
    throw new Error(
      `Cannot build routing sheet: no signing key for active identity '${authorId}'`
    );
  }
  // `exactOptionalPropertyTypes` makes `systemIdentity: undefined` distinct
  // from "no `systemIdentity` key" — spread the optional field only if
  // populated to satisfy the stricter type.
  const baseParams = {
    documentId: keyObject.id,
    dk: keyObject.key,
    author: {
      identityId: authorId,
      kemPublicKey: publicKeys.author.kemPublicKey,
      signingKeyFingerprint: publicKeys.author.signingKeyFingerprint,
      signingPrivateKey,
    },
    // F14 boundary: disclosure-layer "participants" maps to F14-primitive
    // "recipients". The F14 vocabulary is the cryptographic-primitive layer
    // (every party who wraps a DK is a "recipient" of the encapsulated key);
    // leaving its parameter name alone keeps the abstraction boundary visible.
    recipients: participants.map(p => ({
      identityId: p.email,
      kemPublicKey: publicKeys.participants[p.email]!.kemPublicKey,
      signingKeyFingerprint:
        publicKeys.participants[p.email]!.signingKeyFingerprint,
    })),
  };
  return buildRoutingSheet(
    publicKeys.system
      ? {
          ...baseParams,
          systemIdentity: {
            identityId: SYSTEM_IDENTITY,
            kemPublicKey: publicKeys.system.kemPublicKey,
            signingKeyFingerprint: publicKeys.system.signingKeyFingerprint,
          },
        }
      : baseParams
  );
};

/** Wire-format encoding of a `RoutingSheet` for the disclosure JSON payload. */
export interface RoutingSheetWirePayload {
  documentId: string;
  algorithmId: string;
  authorId: string;
  authorFingerprint: string;
  signature: string;
  signingAlgorithm: string;
  participants: Array<{
    identityId: string;
    algorithmId: string;
    kemCiphertext: string;
    wrappedDk: string;
    nonce: string;
    recipientFingerprint: string;
    aad: string;
  }>;
}

/**
 * Encode a routing sheet's binary fields into base64nopad wire form for the
 * disclosure JSON payload. The server treats every byte field as opaque
 * (B3 / app-backend#38); only the shape is enforced server-side. AEAD
 * verification happens on the recipient side at unwrap time.
 */
const routingSheetToWirePayload = (
  sheet: RoutingSheet
): RoutingSheetWirePayload => ({
  documentId: sheet.documentId,
  algorithmId: sheet.algorithmId,
  authorId: sheet.authorId,
  authorFingerprint: base64nopad.encode(sheet.authorFingerprint),
  signature: base64nopad.encode(sheet.signature),
  signingAlgorithm: sheet.signingAlgorithm,
  participants: sheet.entries.map(entry => ({
    identityId: entry.identityId,
    algorithmId: entry.algorithmId,
    kemCiphertext: base64nopad.encode(entry.kemCiphertext),
    wrappedDk: base64nopad.encode(entry.wrappedDk),
    nonce: base64nopad.encode(entry.nonce),
    recipientFingerprint: base64nopad.encode(entry.recipientFingerprint),
    aad: base64nopad.encode(entry.aad),
  })),
});

/**
 * localStorage key prefix for in-flight disclosure recovery records
 * (#247). One entry per `processStory` run, written at step 7 and
 * removed at step 10 (`cleanup`) after both server writes succeeded.
 */
export const DISCLOSURE_RECOVERY_KEY_PREFIX = 'td.recovery.disclosure.v1.';

/**
 * Persist the disclosure metadata locally under a fresh correlation id
 * (#247, resolving the #147 placeholder). If the pipeline dies between
 * here and the server writes, the record shows what was in flight so a
 * recovery flow can resume or the user can be told what was lost.
 *
 * Contents are trigger metadata + participant stubs (names/emails) —
 * data the author typed and the server receives in plaintext anyway;
 * no key material and no story content is written. Best-effort: an
 * unavailable/full localStorage logs a warning and does not fail the
 * pipeline (the record is an aid, not a dependency).
 */
const saveDisclosureDataLocally = async (
  disclosureData: DisclosureData
): Promise<string> => {
  const disclosureId = crypto.randomUUID();
  try {
    localStorage.setItem(
      `${DISCLOSURE_RECOVERY_KEY_PREFIX}${disclosureId}`,
      JSON.stringify(disclosureData)
    );
    console.log('Disclosure recovery record saved locally:', disclosureId);
  } catch (error) {
    console.warn('Local disclosure recovery record skipped:', error);
  }
  return disclosureId;
};

interface DisclosureData {
  participants: ParticipantStub[];
  whenType: string | undefined;
  checkInValue: number | undefined;
  checkInUnit: string | undefined;
}

const saveStoryToServer = async (
  storyPayload: StoryPayload
): Promise<SavedStoryResult> => {
  console.log('Saving story to server');
  const response = await tdFetch('/api/story', {
    method: 'POST',
    body: JSON.stringify(storyPayload),
  });
  if (!response.ok) {
    throw new Error(`Failed to save story: ${response.statusText}`);
  }
  const result = (await response.json()) as SavedStoryResult;
  console.log('Story saved to server:', result.id);
  return result;
};

interface DisclosureServerPayload extends DisclosureData {
  routingSheet: RoutingSheetWirePayload;
  /** Capability reference to the encrypted story record (#240). */
  storyId: string;
  /** Story content checksum — the second half of the capability token. */
  storyChecksum?: string;
}

const saveDisclosureToServer = async (
  disclosureData: DisclosureServerPayload
): Promise<SavedDisclosureResult> => {
  console.log('Saving disclosure to server');
  const response = await tdFetch('/api/disclosure', {
    method: 'POST',
    body: JSON.stringify(disclosureData),
  });
  if (!response.ok) {
    throw new Error(`Failed to save disclosure: ${response.statusText}`);
  }
  const result = (await response.json()) as SavedDisclosureResult;
  console.log('Disclosure saved to server:', result.id);
  return result;
};

/**
 * Final housekeeping after both server writes succeeded (#247, replacing
 * the #147 placeholder):
 *
 *   1. Remove the step-7 disclosure recovery record — the server now
 *      holds the canonical disclosure, nothing is in flight anymore.
 *   2. Save the vault if it accumulated unsaved changes since step 2
 *      (typically first-seen TOFU pins from step 4), so the pins and any
 *      other mutations reach the server + local envelope backup.
 */
const cleanupLocalData = async (
  storyId: string,
  disclosureId: string
): Promise<void> => {
  console.log('Cleaning up local data for story:', storyId);
  try {
    localStorage.removeItem(`${DISCLOSURE_RECOVERY_KEY_PREFIX}${disclosureId}`);
  } catch (error) {
    console.warn('Local disclosure recovery record removal skipped:', error);
  }
  if (vaultService.hasUnsavedChanges()) {
    await vaultService.saveVault();
  }
};

// -----------------------------------------------------------------------------
// Orchestrator
// -----------------------------------------------------------------------------

/**
 * Main story processing function with tickler tracking.
 *
 * @param submission Story submission data from `TdStoryForm.harvestData`.
 * @param tickler Optional tickler for resuming a failed prior run. If
 *   omitted a fresh tickler is created; completed steps are skipped on
 *   resume.
 */
export const processStory = async (
  submission: StorySubmission,
  tickler: StoryProcessingTickler | null = null
): Promise<ProcessStoryResult> => {
  const activeTickler = tickler ?? createStoryProcessingTickler();

  try {
    console.log('Processing story with tickler:', activeTickler.getStatus());

    // #240: the bytes that get encrypted (step 3) and signed (step 5)
    // are the versioned `{v, title, content}` envelope — the title is
    // end-to-end encrypted alongside the content, and the load path
    // (`story.ts` / `getStoryData`) recovers it after decryption.
    const storyPlaintext = encodeStoryPlaintext({
      title: typeof submission.title === 'string' ? submission.title : '',
      content: submission.content,
    });

    // #145: `participants` is the canonical name end-to-end. The *value*
    // at this stage is still form-side participant stubs; the routing-sheet
    // entries the backend's `DisclosureParticipant` schema expects are
    // produced at step 9 below.
    const disclosureData: DisclosureData = {
      participants: submission.participants ?? [],
      whenType: submission.whenType,
      checkInValue: submission.checkInValue,
      checkInUnit: submission.checkInUnit,
    };

    // Step 1: Generate symmetric key
    if (activeTickler.getStepStatus('generateSymmetricKey') === 'pending') {
      activeTickler.markStepInProgress('generateSymmetricKey');
      const keyObject = await generateStorySymmetricKey();
      activeTickler.markStepCompleted('generateSymmetricKey', keyObject);
    }

    // Step 2: Save symmetric key to vault
    if (activeTickler.getStepStatus('saveSymmetricKey') === 'pending') {
      activeTickler.markStepInProgress('saveSymmetricKey');
      const keyObject = requireStepData(activeTickler, 'generateSymmetricKey');
      await saveSymmetricKeyToVault(keyObject);
      activeTickler.markStepCompleted('saveSymmetricKey');
    }

    // Step 3: Encrypt story content
    if (activeTickler.getStepStatus('encryptStory') === 'pending') {
      activeTickler.markStepInProgress('encryptStory');
      const keyObject = requireStepData(activeTickler, 'generateSymmetricKey');
      // F13: bind ciphertext to (documentId, version, algorithmId) via AAD.
      // The DK's UUID is also the documentId — recipients use it to
      // reconstruct AAD at decrypt time.
      const encryptedStory = await encryptStoryContent(
        storyPlaintext,
        keyObject.key,
        keyObject.id,
        1
      );
      const stepData: EncryptedStoryStepData = {
        nonce: encryptedStory.nonce,
        ciphertext: encryptedStory.ciphertext,
        documentId: keyObject.id,
        version: 1,
      };
      activeTickler.markStepCompleted('encryptStory', stepData);
    }

    // Step 4: Request signed public-key bundles + TOFU-pin (F12-wiring / #83)
    if (activeTickler.getStepStatus('requestPublicKeys') === 'pending') {
      activeTickler.markStepInProgress('requestPublicKeys');
      const contents = vaultService.currentContents();
      if (contents === null) {
        throw new Error(
          'Vault must be unlocked to fetch signed public-key bundles'
        );
      }

      const candidateAuthorId =
        submission.authorId ??
        (contents as { activeIdentityId?: unknown }).activeIdentityId ??
        (contents as { activeIdentity?: { id?: unknown } }).activeIdentity?.id;

      if (
        typeof candidateAuthorId !== 'string' ||
        candidateAuthorId.trim() === ''
      ) {
        throw new Error(
          'Unable to determine author identity for signed public-key bundle fetch; provide submission.authorId or unlock a vault with an active identity'
        );
      }

      const { publicKeys, contents: updatedContents } = await requestPublicKeys(
        contents,
        disclosureData.participants,
        candidateAuthorId
      );
      // Persist any first-seen pins to the in-memory vault so the next
      // `vaultService.save()` writes them to the server.
      vaultService.updateContents(updatedContents);
      activeTickler.markStepCompleted('requestPublicKeys', publicKeys);
    }

    // Step 5: Sign story
    if (activeTickler.getStepStatus('signStory') === 'pending') {
      activeTickler.markStepInProgress('signStory');
      // #240: sign the exact bytes that were encrypted — the plaintext
      // envelope — so signature verification and decryption agree on
      // one canonical message.
      const signedStory = await signStoryContent(storyPlaintext);
      activeTickler.markStepCompleted('signStory', signedStory);
    }

    // Step 6: Build F14 routing sheet
    if (activeTickler.getStepStatus('encryptSymmetricKeys') === 'pending') {
      activeTickler.markStepInProgress('encryptSymmetricKeys');
      const keyObject = requireStepData(activeTickler, 'generateSymmetricKey');
      const publicKeys = requireStepData(activeTickler, 'requestPublicKeys');
      const routingSheet = buildSubmissionRoutingSheet({
        keyObject,
        publicKeys,
        participants: disclosureData.participants,
      });
      activeTickler.markStepCompleted('encryptSymmetricKeys', routingSheet);
    }

    // Step 7: Save disclosure recovery record locally (#247)
    if (activeTickler.getStepStatus('saveDisclosureLocal') === 'pending') {
      activeTickler.markStepInProgress('saveDisclosureLocal');
      const disclosureId = await saveDisclosureDataLocally(disclosureData);
      activeTickler.markStepCompleted('saveDisclosureLocal', { disclosureId });
    }

    // Step 8: Save story to server
    if (activeTickler.getStepStatus('saveStoryServer') === 'pending') {
      activeTickler.markStepInProgress('saveStoryServer');
      const encryptedStory = requireStepData(activeTickler, 'encryptStory');
      const storyPayload = createStoryPayload({
        nonce: encryptedStory.nonce,
        ciphertext: encryptedStory.ciphertext,
        documentId: encryptedStory.documentId,
        version: encryptedStory.version,
      });
      const storyResult = await saveStoryToServer(storyPayload);
      // #240: keep the checksum with the result — step 9 sends the
      // (storyId, checksum) capability reference on the disclosure so
      // the list view can open the story later. Client-computed value
      // is the fallback; the server-echoed one wins when present.
      activeTickler.markStepCompleted('saveStoryServer', {
        checksum: storyPayload.checksum,
        ...storyResult,
      });
    }

    // Step 9: Save disclosure to server — F14 routing sheet wire-encoded
    // inline so the server sees {participants, routingSheet} in one shot;
    // server enforces shape only (B3 / app-backend#38) and never decrypts
    // the wrapped DKs.
    if (activeTickler.getStepStatus('saveDisclosureServer') === 'pending') {
      activeTickler.markStepInProgress('saveDisclosureServer');
      const routingSheet = requireStepData(
        activeTickler,
        'encryptSymmetricKeys'
      );
      // #240: link the disclosure to its encrypted story via the
      // capability-token reference. The backend persists these once
      // app-backend#155 lands; today its
      // `@JsonIgnoreProperties(ignoreUnknown)` drops them harmlessly.
      const storyResult = requireStepData(activeTickler, 'saveStoryServer');
      const disclosurePayload: DisclosureServerPayload = {
        ...disclosureData,
        storyId: storyResult.id,
        ...(typeof storyResult.checksum === 'string'
          ? { storyChecksum: storyResult.checksum }
          : {}),
        routingSheet: routingSheetToWirePayload(routingSheet),
      };
      const disclosureResult = await saveDisclosureToServer(disclosurePayload);
      activeTickler.markStepCompleted('saveDisclosureServer', disclosureResult);
    }

    // Step 10: Cleanup
    if (activeTickler.getStepStatus('cleanup') === 'pending') {
      activeTickler.markStepInProgress('cleanup');
      const storyResult = requireStepData(activeTickler, 'saveStoryServer');
      const { disclosureId } = requireStepData(
        activeTickler,
        'saveDisclosureLocal'
      );
      await cleanupLocalData(storyResult.id, disclosureId);
      activeTickler.markStepCompleted('cleanup');
    }

    console.log('Story processing completed successfully');
    return { success: true, tickler: activeTickler };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('Error processing story:', err);

    // Mark current step as failed
    const currentStep = (Object.keys(activeTickler.steps) as StepName[]).find(
      step => activeTickler.getStepStatus(step) === 'in_progress'
    );
    if (currentStep) {
      activeTickler.markStepFailed(currentStep, err);
    }

    return { success: false, tickler: activeTickler, error: err };
  }
};

/**
 * Internal helper: read a tickler step's data, throwing if it's missing.
 * Steps are populated by earlier branches in the orchestrator and only
 * read once their producer has run, so the throw is purely a tightened
 * type narrowing (`StepDataMap[K]` rather than `... | undefined`).
 */
const requireStepData = <K extends StepName>(
  tickler: StoryProcessingTickler,
  step: K
) => {
  const data = tickler.getStepData(step);
  if (data === undefined) {
    throw new Error(
      `Internal pipeline error: step "${step}" data is unexpectedly missing`
    );
  }
  return data;
};
